/**
 * Achievement system — Tracks player milestones and shows toast notifications.
 * 30+ achievements with unlock conditions checked each frame.
 */

const STORAGE_KEY = 'voxelgame_achievements';

// Achievement definitions
const ACHIEVEMENTS = [
  // Basics
  { id: 'first_wood',    name: 'Leñador',             desc: 'Rompe tu primer tronco',           icon: '🪵' },
  { id: 'first_craft',   name: 'Artesano',            desc: 'Craftea tu primer objeto',          icon: '🔨' },
  { id: 'first_stone',   name: 'Edad de Piedra',      desc: 'Mina piedra con un pico',           icon: '⛏️' },
  { id: 'first_furnace', name: 'Fundidor',            desc: 'Construye un horno',                icon: '🔥' },
  { id: 'first_iron',    name: 'Adquiere Hierro',     desc: 'Consigue un lingote de hierro',     icon: '🪙' },
  { id: 'first_diamond', name: '¡Diamantes!',         desc: 'Encuentra un diamante',             icon: '💎' },

  // Mining
  { id: 'mine_coal',     name: 'Minero de Carbón',    desc: 'Mina 10 bloques de carbón',         icon: '⚫' },
  { id: 'mine_gold',     name: 'Fiebre del Oro',      desc: 'Encuentra oro',                     icon: '🥇' },
  { id: 'mine_emerald',  name: 'Joyero',              desc: 'Encuentra una esmeralda',           icon: '💚' },
  { id: 'mine_redstone', name: 'Ingeniero',           desc: 'Mina redstone',                     icon: '🔴' },
  { id: 'mine_deep',     name: 'Explorador Profundo', desc: 'Llega a Y=10 o menor',              icon: '⬇️' },

  // Combat
  { id: 'kill_zombie',   name: 'Cazador de Zombies',  desc: 'Mata tu primer zombie',             icon: '🧟' },
  { id: 'kill_skeleton', name: 'Contra-Sniper',       desc: 'Mata un esqueleto',                 icon: '💀' },
  { id: 'kill_creeper',  name: '¡Ssss... Boom!',      desc: 'Mata un creeper',                   icon: '💥' },
  { id: 'kill_spider',   name: 'Aracnofobia',         desc: 'Mata una araña',                    icon: '🕷️' },
  { id: 'kill_10',       name: 'Guerrero',            desc: 'Mata 10 mobs hostiles',             icon: '⚔️' },

  // Survival
  { id: 'first_night',   name: 'Primera Noche',       desc: 'Sobrevive tu primera noche',        icon: '🌙' },
  { id: 'eat_food',      name: 'Buen Provecho',       desc: 'Come tu primera comida',            icon: '🍖' },
  { id: 'full_health',   name: 'Vida Plena',          desc: 'Recupera toda tu salud',            icon: '❤️' },
  { id: 'near_death',    name: 'Al Borde',            desc: 'Sobrevive con 1 corazón',           icon: '💔' },

  // Building
  { id: 'place_100',     name: 'Constructor',         desc: 'Coloca 100 bloques',                icon: '🧱' },
  { id: 'place_500',     name: 'Arquitecto',          desc: 'Coloca 500 bloques',                icon: '🏗️' },
  { id: 'place_torch',   name: 'Iluminador',          desc: 'Coloca tu primera antorcha',        icon: '🔦' },

  // Exploration
  { id: 'visit_villa_m', name: 'Turista Pobre',       desc: 'Visita Villa Miseria',              icon: '🏚️' },
  { id: 'visit_villa_p', name: 'Turista Rico',        desc: 'Visita Villa Prosperidad',          icon: '🏰' },
  { id: 'climb_high',    name: 'Montañista',          desc: 'Llega a Y=80 o mayor',              icon: '🏔️' },
  { id: 'swim',          name: 'Nadador',             desc: 'Nada por primera vez',              icon: '🏊' },
  { id: 'fly',           name: 'Volador',             desc: 'Activa el modo vuelo',              icon: '🦅' },

  // Crafting
  { id: 'craft_diamond_pick', name: 'Pico de Diamante', desc: 'Craftea un pico de diamante',   icon: '💠' },
  { id: 'craft_iron_tools',   name: 'Herrero',          desc: 'Craftea herramientas de hierro', icon: '🛠️' },

  // Nether
  { id: 'enter_nether',  name: 'Al Infierno',         desc: 'Entra al Nether por primera vez',   icon: '🔥' },
  { id: 'kill_ghast',    name: 'Caza-Fantasmas',      desc: 'Mata un Ghast',                     icon: '👻' },
  { id: 'kill_blaze',    name: 'Apaga-Fuegos',        desc: 'Mata un Blaze',                     icon: '🔶' },
  { id: 'nether_return', name: 'Ida y Vuelta',        desc: 'Regresa del Nether con vida',       icon: '🌀' },

  // The End
  { id: 'enter_end',      name: 'El Fin',              desc: 'Entra al End por primera vez',      icon: '🐉' },
  { id: 'kill_dragon',    name: '¡Libertad!',          desc: 'Derrota al Dragón del End',         icon: '👑' },
  { id: 'find_stronghold',name: 'Explorador',          desc: 'Encuentra una fortaleza',           icon: '🏰' },
  { id: 'find_elytra',    name: 'Alas de Libertad',    desc: 'Encuentra unos élitros',            icon: '🦇' },
  { id: 'end_return',     name: 'Viaje Completo',      desc: 'Regresa del End con vida',          icon: '🌍' },

  // Special
  { id: 'chest_hoard',   name: 'Acumulador',          desc: 'Llena un cofre completo',           icon: '📦' },
  { id: 'trade',         name: 'Comerciante',         desc: 'Comercia con un aldeano',           icon: '🤝' },
  { id: 'weather_storm', name: 'Tormenta',            desc: 'Sobrevive una tormenta',            icon: '⛈️' },
];

export class AchievementSystem {
  constructor() {
    this.unlocked = new Set();
    this._counters = {
      blocksPlaced: 0,
      blocksMined: 0,
      coalMined: 0,
      hostileKills: 0,
      nightsSurvived: 0,
    };
    this._toastQueue = [];
    this._currentToast = null;
    this._toastTimer = 0;
    this._nightStarted = false;
    this._soundManager = null;

    this._load();
    this._buildToastEl();
  }

  /** Connect sound manager for achievement unlock fanfare */
  setSoundManager(sm) {
    this._soundManager = sm;
  }

  _buildToastEl() {
    this.toastEl = document.createElement('div');
    this.toastEl.style.cssText = `
      position: fixed; top: 40px; left: 50%;
      transform: translateX(-50%) translateY(-100px);
      background: rgba(20, 20, 20, 0.9);
      border: 2px solid #FFD700;
      border-radius: 6px;
      padding: 10px 20px;
      color: #FFD700;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      z-index: 800;
      pointer-events: none;
      transition: transform 0.4s ease-out, opacity 0.4s ease-out;
      opacity: 0;
      text-align: center;
      min-width: 250px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
    `;
    document.body.appendChild(this.toastEl);
  }

  unlock(id) {
    if (this.unlocked.has(id)) return false;
    const def = ACHIEVEMENTS.find(a => a.id === id);
    if (!def) return false;

    this.unlocked.add(id);
    this._save();

    // Queue toast notification
    this._toastQueue.push(def);

    // Fullscreen celebration (called via callback to avoid circular imports)
    if (this.onUnlockCelebration) {
      this.onUnlockCelebration(def);
    }
    return true;
  }

  update(dt) {
    // Process toast queue
    if (this._currentToast) {
      this._toastTimer -= dt;
      if (this._toastTimer <= 0) {
        // Hide current toast
        this.toastEl.style.transform = 'translateX(-50%) translateY(-100px)';
        this.toastEl.style.opacity = '0';
        this._currentToast = null;
      }
    } else if (this._toastQueue.length > 0) {
      const toast = this._toastQueue.shift();
      this._currentToast = toast;
      this._toastTimer = 4; // Show for 4 seconds

      this.toastEl.innerHTML = `
        <div style="font-size: 10px; color: #AAA; margin-bottom: 2px;">LOGRO DESBLOQUEADO</div>
        <div style="font-size: 20px; margin-bottom: 2px;">${toast.icon}</div>
        <div style="font-weight: bold;">${toast.name}</div>
        <div style="font-size: 11px; color: #CCC;">${toast.desc}</div>
      `;

      // Animate in
      requestAnimationFrame(() => {
        this.toastEl.style.transform = 'translateX(-50%) translateY(0)';
        this.toastEl.style.opacity = '1';
      });
      // Play achievement sound
      if (this._soundManager) this._soundManager.playAchievementUnlock();
    }
  }

  // --- Event handlers (called from game systems) ---

  onBlockMined(blockId) {
    this._counters.blocksMined++;
    if (blockId === 5) this.unlock('first_wood');   // WOOD
    if (blockId === 3) this.unlock('first_stone');   // STONE
    if (blockId === 18) { // COAL_ORE
      this._counters.coalMined++;
      if (this._counters.coalMined >= 10) this.unlock('mine_coal');
    }
    if (blockId === 20) this.unlock('mine_gold');    // GOLD_ORE
    if (blockId === 21) this.unlock('first_diamond'); // DIAMOND_ORE
    if (blockId === 24) this.unlock('mine_emerald'); // EMERALD_ORE
    if (blockId === 22) this.unlock('mine_redstone'); // REDSTONE_ORE
    this._scheduleSave();
  }

  onBlockPlaced(blockId) {
    this._counters.blocksPlaced++;
    if (blockId === 26) this.unlock('place_torch');   // TORCH
    if (blockId === 28) this.unlock('first_furnace'); // FURNACE
    if (this._counters.blocksPlaced >= 100) this.unlock('place_100');
    if (this._counters.blocksPlaced >= 500) this.unlock('place_500');
    this._scheduleSave();
  }

  onCraft(result) {
    this.unlock('first_craft');
    if (result && result.toolType === 'pickaxe' && result.material === 'diamond') {
      this.unlock('craft_diamond_pick');
    }
    if (result && result.material === 'iron') {
      this.unlock('craft_iron_tools');
    }
  }

  onSmelt() {
    this.unlock('first_iron');
  }

  onKillMob(type) {
    this._counters.hostileKills++;
    if (type === 'ZOMBIE') this.unlock('kill_zombie');
    if (type === 'SKELETON') this.unlock('kill_skeleton');
    if (type === 'CREEPER') this.unlock('kill_creeper');
    if (type === 'SPIDER') this.unlock('kill_spider');
    if (type === 'GHAST') this.unlock('kill_ghast');
    if (type === 'BLAZE') this.unlock('kill_blaze');
    if (this._counters.hostileKills >= 10) this.unlock('kill_10');
    this._scheduleSave();
  }

  onEatFood() {
    this.unlock('eat_food');
  }

  onTrade() {
    this.unlock('trade');
  }

  onPositionUpdate(y) {
    if (y <= 10) this.unlock('mine_deep');
    if (y >= 80) this.unlock('climb_high');
  }

  onSwim() {
    this.unlock('swim');
  }

  onFly() {
    this.unlock('fly');
  }

  onVisitVillaMiseria() {
    this.unlock('visit_villa_m');
  }

  onVisitVillaProspera() {
    this.unlock('visit_villa_p');
  }

  onHealthUpdate(health, maxHealth) {
    if (health >= maxHealth) this.unlock('full_health');
    if (health > 0 && health <= 2) this.unlock('near_death');
  }

  onNightStart() {
    this._nightStarted = true;
  }

  onDayStart() {
    if (this._nightStarted) {
      this.unlock('first_night');
      this._nightStarted = false;
    }
  }

  onWeatherStorm() {
    this.unlock('weather_storm');
  }

  onEnterNether() {
    this.unlock('enter_nether');
  }

  onReturnFromNether() {
    this.unlock('nether_return');
  }

  onEnterEnd() {
    this.unlock('enter_end');
  }

  onDragonDefeat() {
    this.unlock('kill_dragon');
  }

  onFindStronghold() {
    this.unlock('find_stronghold');
  }

  onFindElytra() {
    this.unlock('find_elytra');
  }

  onReturnFromEnd() {
    this.unlock('end_return');
  }

  // --- Stats ---

  getProgress() {
    return { unlocked: this.unlocked.size, total: ACHIEVEMENTS.length };
  }

  getAllAchievements() {
    return ACHIEVEMENTS.map(a => ({
      ...a,
      unlocked: this.unlocked.has(a.id),
    }));
  }

  // --- Persistence ---

  _save() {
    try {
      const data = {
        unlocked: [...this.unlocked],
        counters: this._counters,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* */ }
  }

  _scheduleSave() {
    if (this._saveTimer) return;
    this._saveTimer = setTimeout(() => {
      this._saveTimer = null;
      this._save();
    }, 2000);
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.unlocked) {
        for (const id of data.unlocked) this.unlocked.add(id);
      }
      if (data.counters) {
        Object.assign(this._counters, data.counters);
      }
    } catch (e) { /* */ }
  }
}
